<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
  watchEffect,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import CommentsLayout from './CommentsLayout.vue';
import BaseCommentsHeader from './components/CommentsHeader.vue';
import { formatDate } from 'dashboard/helper/dateHelper.js';
import SwitchComponent from '/dashboard/components-next/switch/Switch.vue';
import CommentsFilter from './components/CommentsFilter.vue';
import Comment from './components/Comment.vue';
import CustomIcon from 'dashboard/components/ui/CustomIcons.vue';
import SuspendMenu from '../../../components/SuspendMenu.vue';
import AITestChat from './components/AITestChat.vue';
import ReplyDialog from 'dashboard/components-next/dialog/Dialog.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

const store = useStore();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const searchWord = ref('');
const showReadMore = ref(false);
const showCommentsFilterDisplayMenu = ref(false);
const filter = ref({});
const pagination = ref({
  perPage: 20,
  currentPage: 1,
});
const showAITestChat = ref(false);

const filterButtonRect = ref(null);
const replyCommentId = ref(null);
const replyTargetId = ref(null);
const replyUsername = ref('');
const replyShouldMention = ref(true);
const replyText = ref('');
const isSubmittingReply = ref(false);
const isLoadingMore = ref(false);
const commentsListRef = ref(null);
const filterButton = ref(null);
const replyDialog = ref(null);
const dmDialog = ref(null);
const dmText = ref('');
const dmTargetCommentId = ref(null);
const dmAuthorName = ref('');
const isSubmittingDm = ref(false);
const videoElement = ref(null);
const showRemovePostModal = ref(false);
const deletedPostInfo = ref(null);
const isRemovingPost = ref(false);
const deletedCommentDialog = ref(null);
const pendingCommentDeletion = ref(null);
const backUrl = computed(() => {
  return route.query.returnTo || null;
});

const postInfo = computed(() => store.getters['comments/getPostInfo']);
const commentsList = computed(() => store.getters['comments/getPostComments']);
const uiFlags = computed(() => store.getters['comments/getUIFlags']);
const customClassifications = computed(
  () => store.getters['comments/getCustomClassifications']
);

const isVideo = computed(() => {
  return (
    postInfo.value.image_path && postInfo.value.image_path.endsWith('.mp4')
  );
});

const filterDropdownStyle = computed(() => {
  if (!filterButtonRect.value) return { display: 'none' };

  // Validar que temos valores válidos
  if (
    !filterButtonRect.value.bottom ||
    !filterButtonRect.value.right ||
    !filterButtonRect.value.top
  ) {
    return { display: 'none' };
  }

  const isRTL = document.dir === 'rtl';

  // Calcula espaço disponível abaixo e acima do botão
  const spaceBelow = Math.max(
    0,
    window.innerHeight - filterButtonRect.value.bottom
  );
  const spaceAbove = Math.max(0, filterButtonRect.value.top);
  const dropdownHeight = 500; // Altura estimada do dropdown (inclui padding e header)

  // Decide se abre para cima ou para baixo
  const openUpwards = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

  const rightPosition = Math.max(
    0,
    window.innerWidth - filterButtonRect.value.right
  );

  const styles = {
    [isRTL ? 'left' : 'right']: `${rightPosition}px`,
  };

  if (openUpwards) {
    // Abre para cima
    const bottomPosition = Math.max(
      0,
      window.innerHeight - filterButtonRect.value.top + 8
    );
    styles.bottom = `${bottomPosition}px`;
  } else {
    // Abre para baixo
    const topPosition = Math.max(0, filterButtonRect.value.bottom + 8);
    styles.top = `${topPosition}px`;
  }

  return styles;
});

const filterContentMaxHeight = computed(() => {
  if (!filterButtonRect.value) return '400px';

  const spaceBelow = window.innerHeight - filterButtonRect.value.bottom;
  const spaceAbove = filterButtonRect.value.top;
  const headerHeight = 80; // Altura do header do filtro (sort section)
  const padding = 32; // Padding total + margem de segurança

  const openUpwards = spaceBelow < 500 && spaceAbove > spaceBelow;
  const availableSpace = openUpwards ? spaceAbove : spaceBelow;

  return `${Math.max(200, Math.min(400, availableSpace - headerHeight - padding))}px`;
});

const hasMoreComments = computed(() => {
  return (
    commentsList.value.pagination?.currentPage > 0 &&
    commentsList.value.pagination?.currentPage <
      commentsList.value.pagination?.totalPages
  );
});

// Avoid replacing the whole post + comments UI when createReply toggles isFetching
const isCommentsShellLoading = computed(
  () =>
    uiFlags.value.isFetching &&
    pagination.value.currentPage === 1 &&
    !isSubmittingReply.value
);

const formatFilterToRequest = filterObj => {
  return {
    sort: filterObj.sort?.key ?? 'desc',
    classification: filterObj.classification?.map(item => item.key) ?? [],
    customClassifications:
      filterObj.customClassifications?.map(item => item.key) ?? [],
  };
};

let fetchComments = null;

const loadMoreComments = async () => {
  if (isLoadingMore.value || !hasMoreComments.value) return;

  isLoadingMore.value = true;
  pagination.value.currentPage += 1;

  try {
    await fetchComments();
  } finally {
    isLoadingMore.value = false;
  }
};

const handleScroll = () => {
  const container = commentsListRef.value;
  if (!container) return;

  const scrollTop = container.scrollTop;
  const scrollHeight = container.scrollHeight;
  const clientHeight = container.clientHeight;
  const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 100;

  if (
    scrolledToBottom &&
    hasMoreComments.value &&
    !isLoadingMore.value &&
    !uiFlags.value.isFetching
  ) {
    loadMoreComments();
  }
};

// watchEffect para configurar o scroll listener quando o ref estiver disponível
watchEffect(() => {
  if (commentsListRef.value && commentsList.value.comments?.length > 0) {
    commentsListRef.value.removeEventListener('scroll', handleScroll);
    commentsListRef.value.addEventListener('scroll', handleScroll);
  }
});

fetchComments = async () => {
  const filtersToApply = formatFilterToRequest(filter.value);

  await store.dispatch('comments/getPostComments', {
    pageId: route.params.page_id,
    postId: route.params.post_id,
    filters: filtersToApply,
    commentText: searchWord.value,
    pagination: pagination.value,
  });

  await nextTick();
  // Scroll listener é configurado automaticamente via watchEffect
};

const fetchPostInfo = async () => {
  await store.dispatch('comments/getPostInfo', {
    pageId: route.params.page_id,
    postId: route.params.post_id,
  });
};

const openDeletedPostModal = info => {
  deletedPostInfo.value = info;
  showRemovePostModal.value = true;
};

const refreshPostComments = async () => {
  try {
    await fetchPostInfo();
  } catch (e) {
    if (e.deletedPost) {
      openDeletedPostModal(e.deletedPost);
      return;
    }
    throw e;
  }
  await fetchComments();
};

const handleReadMore = () => {
  showReadMore.value = !showReadMore.value;
};

const onFilterChange = option => {
  filter.value = option;
  pagination.value.currentPage = 1;
  showCommentsFilterDisplayMenu.value = false;
  fetchComments();
};

const onSearch = searchText => {
  searchWord.value = searchText;
  pagination.value.currentPage = 1;
  fetchComments();
};

const onToggleActive = () => {
  store.dispatch('posts/toggleActive', {
    pageId: route.params.page_id,
    postId: route.params.post_id,
  });
};

const openCommentsFilterDisplayMenu = () => {
  showCommentsFilterDisplayMenu.value = !showCommentsFilterDisplayMenu.value;
  if (showCommentsFilterDisplayMenu.value) {
    nextTick(() => {
      const button = filterButton.value;
      if (button) {
        const rect = button.getBoundingClientRect();
        // Validar que o rect tem valores válidos
        if (
          rect &&
          rect.top !== undefined &&
          rect.bottom !== undefined &&
          rect.right !== undefined
        ) {
          filterButtonRect.value = rect;
        } else {
          // Fallback: fechar o menu se não conseguir obter posição
          showCommentsFilterDisplayMenu.value = false;
        }
      } else {
        // Fallback: fechar o menu se não encontrar o botão
        showCommentsFilterDisplayMenu.value = false;
      }
    });
  } else {
    // Limpar rect quando fechar
    filterButtonRect.value = null;
  }
};

const handleTestMode = () => {
  showAITestChat.value = true;
};

const hideAITestChat = () => {
  showAITestChat.value = false;
};

const togglePlay = event => {
  const video = videoElement.value;
  if (!video) return;
  if (video.paused) {
    video.play();
  } else {
    video.pause();
  }
  event.stopPropagation();
};

const findCommentById = commentId => {
  const comments = commentsList.value?.comments || [];
  const idEquals = (a, b) => String(a) === String(b);

  const top = comments.find(c => c && idEquals(c.id, commentId));
  if (top) return top;

  let reply = null;
  comments.some(c => {
    reply = (c?.replies || []).find(r => r && idEquals(r.id, commentId));
    return !!reply;
  });
  return reply;
};

const verifyCommentExists = async commentId => {
  try {
    await store.dispatch('comments/verifyCommentExists', {
      commentId,
      pageId: route.params.page_id,
      postId: route.params.post_id,
    });
    return { exists: true };
  } catch (error) {
    const { status, data } = error.response || {};
    // 410 (deleted on platform) and 404 (missing locally) both mean the comment is gone
    if ((status === 410 || status === 404) && data?.deleted) {
      return { exists: false, deletedCommentId: data.comment_id || commentId };
    }
    // For other errors, assume the comment exists (don't block the user)
    return { exists: true };
  }
};

// Returns false and opens the confirmation dialog when the comment no longer exists
const ensureCommentExists = async (commentId, action) => {
  const verification = await verifyCommentExists(commentId);
  if (verification.exists) return true;

  pendingCommentDeletion.value = {
    commentId: verification.deletedCommentId || commentId,
    action,
    isMentioned: findCommentById(commentId)?.metadata?.type === 'MENTION',
  };
  nextTick(() => deletedCommentDialog.value?.open());
  return false;
};

const deletedCommentDialogCopy = computed(() => {
  const { action, isMentioned } = pendingCommentDeletion.value || {};

  if (action === 'reply') {
    return isMentioned
      ? {
          title: t('COMMENTS.MODERATION.REPLY_FAILED_MENTIONED.TITLE'),
          description: t(
            'COMMENTS.MODERATION.REPLY_FAILED_MENTIONED.DESCRIPTION'
          ),
        }
      : {
          title: t('COMMENTS.MODERATION.REPLY_FAILED_OWNED.TITLE'),
          description: t('COMMENTS.MODERATION.REPLY_FAILED_OWNED.DESCRIPTION'),
        };
  }

  if (action === 'dm') {
    return isMentioned
      ? {
          title: t('COMMENTS.MODERATION.DM_FAILED_MENTIONED.TITLE'),
          description: t('COMMENTS.MODERATION.DM_FAILED_MENTIONED.DESCRIPTION'),
        }
      : {
          title: t('COMMENTS.MODERATION.DM_FAILED_OWNED.TITLE'),
          description: t('COMMENTS.MODERATION.DM_FAILED_OWNED.DESCRIPTION'),
        };
  }

  return {
    title: t('COMMENTS.MODERATION.HIDE_CONFIRMATION.TITLE'),
    description: t('COMMENTS.MODERATION.HIDE_CONFIRMATION.DESCRIPTION'),
  };
});

const confirmRemoveDeletedComment = async () => {
  const commentId = pendingCommentDeletion.value?.commentId;
  // close() emits 'close', which clears pendingCommentDeletion
  deletedCommentDialog.value?.close();
  if (!commentId) return;

  try {
    await store.dispatch('comments/delete', {
      commentId,
      pageId: route.params.page_id,
      postId: route.params.post_id,
      callback: () => {},
    });
  } finally {
    fetchComments();
  }
};

const cancelRemoveDeletedComment = () => {
  pendingCommentDeletion.value = null;
};

const deleteComment = async commentId => {
  if (!(await ensureCommentExists(commentId, 'delete'))) return;

  await store.dispatch('comments/delete', {
    commentId,
    pageId: route.params.page_id,
    postId: route.params.post_id,
    callback: () => {},
  });
};

const hideComment = async commentId => {
  if (!(await ensureCommentExists(commentId, 'hide'))) return;

  await store.dispatch('comments/hide', {
    commentId,
    pageId: route.params.page_id,
    postId: route.params.post_id,
    callback: () => {},
  });
};

const openReplyPopup = async (commentId, emittedAuthor = null) => {
  if (!(await ensureCommentExists(commentId, 'reply'))) return;

  const comments = commentsList.value?.comments || [];

  const idEquals = (a, b) => String(a) === String(b);

  let top = comments.find(c => c && idEquals(c.id, commentId));
  let parent = top || null;
  let repliedItem = null;

  if (!top) {
    parent = comments.find(
      c =>
        c &&
        Array.isArray(c.replies) &&
        c.replies.some(r => idEquals(r.id, commentId))
    );
    if (parent) {
      repliedItem = parent.replies.find(r => r && idEquals(r.id, commentId));
    }
  }

  const topLevelId = parent ? parent.id : commentId;

  const resolvedAuthor =
    (repliedItem && (repliedItem.from_name || repliedItem.inbox?.name)) ||
    (parent && (parent.from_name || parent.inbox?.name)) ||
    '';

  const author = emittedAuthor || resolvedAuthor || '';

  const sourceForChannel = parent || top;
  const isFacebook =
    (sourceForChannel?.post?.source_type || '').toUpperCase() === 'FACEBOOK';

  replyTargetId.value = commentId;
  replyCommentId.value = topLevelId;
  replyUsername.value = author;
  replyShouldMention.value = !isFacebook;
  replyText.value = '';
  replyDialog.value?.open();
};

const submitReply = async () => {
  isSubmittingReply.value = true;
  try {
    const comments = commentsList.value?.comments || [];
    const clickedId = replyTargetId.value || replyCommentId.value;

    const idEquals = (a, b) => String(a) === String(b);

    let top = comments.find(c => c && idEquals(c.id, clickedId));
    let parent = top || null;
    let repliedItem = null;

    if (!top) {
      parent = comments.find(
        c =>
          c &&
          Array.isArray(c.replies) &&
          c.replies.some(r => idEquals(r.id, clickedId))
      );
      if (parent) {
        repliedItem = parent.replies.find(r => r && idEquals(r.id, clickedId));
      }
    }

    const topLevelId = parent ? parent.id : clickedId;

    const authorNameToUse =
      repliedItem?.from_name ||
      repliedItem?.inbox?.name ||
      replyUsername.value ||
      (parent && (parent.from_name || parent.inbox?.name)) ||
      '';

    const channelSource = parent || top;
    const isFacebook =
      (channelSource?.post?.source_type || '').toUpperCase() === 'FACEBOOK';

    let mention = '';
    if (authorNameToUse) {
      mention = isFacebook ? `${authorNameToUse} ` : `@${authorNameToUse} `;
    }

    const message = `${mention}${replyText.value}`.trim();

    await store.dispatch('comments/createReply', {
      pageId: route.params.page_id,
      postId: route.params.post_id,
      commentId: topLevelId,
      message,
      callback: ({ success }) => {
        if (success) {
          replyText.value = '';
          replyTargetId.value = null;
          replyCommentId.value = null;
          replyUsername.value = '';
          replyShouldMention.value = true;
          replyDialog.value?.close();
          fetchComments();
        }
      },
    });
  } finally {
    isSubmittingReply.value = false;
  }
};

const closeRemovePostModal = () => {
  showRemovePostModal.value = false;
  deletedPostInfo.value = null;
};

const confirmRemovePost = async () => {
  if (!deletedPostInfo.value?.postId) return;
  isRemovingPost.value = true;
  try {
    await store.dispatch('posts/deletePost', {
      pageId: route.params.page_id,
      postId: deletedPostInfo.value.postId,
    });
    closeRemovePostModal();
    const target =
      backUrl.value || route.path.replace(/\/posts\/[^/]+\/?$/, '');
    router.push(target);
  } finally {
    isRemovingPost.value = false;
  }
};

const closeReplyPopup = () => {
  replyText.value = '';
};

const closeDmPopup = () => {
  dmText.value = '';
};

const openDmPopup = async (commentId, emittedAuthor = null) => {
  if (!(await ensureCommentExists(commentId, 'dm'))) return;

  const comments = commentsList.value?.comments || [];
  const idEquals = (a, b) => String(a) === String(b);

  let top = comments.find(c => c && idEquals(c.id, commentId));
  let parent = top || null;
  let repliedItem = null;

  if (!top) {
    parent = comments.find(
      c =>
        c &&
        Array.isArray(c.replies) &&
        c.replies.some(r => idEquals(r.id, commentId))
    );
    if (parent) {
      repliedItem = parent.replies.find(r => r && idEquals(r.id, commentId));
    }
  }

  const resolvedAuthor =
    (repliedItem && (repliedItem.from_name || repliedItem.inbox?.name)) ||
    (parent && (parent.from_name || parent.inbox?.name)) ||
    '';

  const author = emittedAuthor || resolvedAuthor || '';

  dmTargetCommentId.value = commentId;
  dmAuthorName.value = author;
  dmText.value = '';
  dmDialog.value?.open();
};

const submitDm = async () => {
  if (dmTargetCommentId.value == null) return;

  isSubmittingDm.value = true;
  try {
    const result = await store.dispatch('comments/sendDirectMessage', {
      commentId: dmTargetCommentId.value,
      message: dmText.value,
    });

    dmText.value = '';
    dmDialog.value?.close();
    dmTargetCommentId.value = null;
    dmAuthorName.value = '';

    useAlert(t('COMMENTS.MODERATION_CARD.DM_SENT_SUCCESS'));

    const accountId = store.getters.getCurrentAccountId;
    window.open(
      `/app/accounts/${accountId}/conversations/${result.conversation_id}`,
      '_blank'
    );
  } catch (error) {
    useAlert(t('COMMENTS.MODERATION_CARD.DM_SENT_ERROR'));
  } finally {
    isSubmittingDm.value = false;
  }
};

onMounted(async () => {
  store.dispatch('comments/getCustomClassifications');
  try {
    await fetchPostInfo();
    fetchComments();
  } catch (e) {
    if (e.deletedPost) {
      openDeletedPostModal(e.deletedPost);
    }
  }
});

onBeforeUnmount(() => {
  if (commentsListRef.value) {
    commentsListRef.value.removeEventListener('scroll', handleScroll);
  }
});
</script>

<template>
  <CommentsLayout
    :is-loading="isCommentsShellLoading"
    :loading-message="$t('COMMENTS.POST_DETAILS.LOADING')"
    :no-records-found="!postInfo"
    :no-records-message="$t('COMMENTS.POST_DETAILS.NO_POST_FOUND')"
  >
    <template #header>
      <BaseCommentsHeader
        :header-title="$t('COMMENTS.POST_DETAILS.TITLE')"
        feature-name="facebook-page-post-comments"
        show-back-button="true"
        :back-url="backUrl"
        :list-loading="uiFlags.isFetching && !isSubmittingReply"
        @search="onSearch"
        @refresh="refreshPostComments"
      />
    </template>
    <template #body>
      <div class="flex justify-center w-full px-4 py-6 lg:py-8">
        <div class="w-full max-w-3xl">
          <!-- Post Card -->
          <div
            class="bg-white dark:bg-n-solid-2 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden mb-6"
          >
            <!-- Post Settings Header -->
            <div
              class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 px-4 sm:px-6 py-4 border-b border-slate-200 dark:border-slate-700"
            >
              <div class="flex items-center gap-3">
                <h5
                  class="text-sm sm:text-base font-medium text-slate-900 dark:text-white"
                >
                  {{ $t('COMMENTS.POST_DETAILS.ALLOW_REPLIES') }}
                </h5>
              </div>
              <div class="flex items-center gap-2">
                <SwitchComponent
                  v-model="isSwitchOn"
                  :model-value="!!postInfo.active"
                  @click="onToggleActive"
                />
                <SuspendMenu>
                  <ul class="list-none">
                    <li
                      class="rounded px-4 py-2 bg-n-solid-2 text-slate-800 dark:text-slate-200 dark:hover:bg-n-alpha-2 hover:bg-n-alpha-1 cursor-pointer"
                      @click="handleTestMode"
                    >
                      {{ $t('COMMENTS.POST_DETAILS.TEST_MODE') }}
                    </li>
                  </ul>
                </SuspendMenu>
              </div>
            </div>

            <!-- Post Media -->
            <div class="relative bg-slate-100 dark:bg-slate-800">
              <template v-if="isVideo">
                <video
                  ref="videoElement"
                  class="w-full aspect-square object-cover cursor-pointer"
                  :src="postInfo.image_path"
                  :preload="metadata"
                  controls
                  @click.stop="togglePlay"
                />
              </template>
              <template v-else>
                <img
                  class="w-full aspect-square object-cover"
                  :src="postInfo.image_path || '/assets/images/placeholder.svg'"
                  :alt="$t('COMMENTS.POST_DETAILS.POST_IMAGE')"
                />
              </template>
            </div>

            <!-- Post Info -->
            <div class="px-4 sm:px-6 py-4 space-y-4">
              <!-- Publication Date -->
              <div
                class="flex items-center gap-2 text-xs sm:text-sm text-slate-600 dark:text-slate-400"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="flex-shrink-0"
                >
                  <path
                    d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12zm0-1A5 5 0 1 1 8 3a5 5 0 0 1 0 10zM7.5 4.5v4h4v-1h-3v-3h-1z"
                    fill="currentColor"
                  />
                </svg>
                <span>
                  {{
                    $t('COMMENTS.POST_DETAILS.PUBLISHED_ON', {
                      date: formatDate(postInfo.created_at),
                    })
                  }}
                </span>
              </div>

              <!-- Post Message -->
              <div v-if="postInfo.message" class="space-y-2">
                <p
                  class="text-sm sm:text-base text-slate-700 dark:text-slate-300 whitespace-pre-wrap"
                  :class="showReadMore ? '' : 'line-clamp-3'"
                >
                  {{ postInfo.message }}
                </p>
                <button
                  class="text-sm text-woot-500 hover:text-woot-600 dark:text-woot-400 dark:hover:text-woot-300 font-medium transition-colors"
                  @click="handleReadMore()"
                >
                  {{
                    showReadMore
                      ? $t('COMMENTS.POST_DETAILS.SHOW_LESS')
                      : $t('COMMENTS.POST_DETAILS.READ_MORE')
                  }}
                </button>
              </div>

              <!-- Comments Count & Filter -->
              <div
                class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-slate-200 dark:border-slate-700"
              >
                <p
                  class="text-sm sm:text-base font-semibold text-slate-900 dark:text-white"
                >
                  {{
                    $t('COMMENTS.POST_DETAILS.COMMENT_COUNT', {
                      count: commentsList?.pagination?.totalCount || 0,
                    })
                  }}
                </p>
                <div class="relative">
                  <button
                    ref="filterButton"
                    class="group flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg transition-all duration-200 ease-in-out shadow-sm hover:shadow-md border border-slate-200 dark:border-slate-700"
                    :class="{
                      'ring-2 ring-woot-500 ring-offset-1 dark:ring-offset-slate-900 bg-slate-200 dark:bg-slate-700':
                        showCommentsFilterDisplayMenu,
                    }"
                    type="button"
                    @click="openCommentsFilterDisplayMenu"
                  >
                    <CustomIcon
                      group="general"
                      name="FILTER"
                      :color="
                        showCommentsFilterDisplayMenu ? '#667085' : '#5A6169'
                      "
                    />
                    <span class="text-sm font-medium">{{
                      $t('COMMENTS.MODERATION.FILTERS')
                    }}</span>
                    <fluent-icon
                      icon="chevron-down"
                      size="16"
                      class="text-slate-500 dark:text-slate-400 transition-transform duration-200"
                      :class="{ 'rotate-180': showCommentsFilterDisplayMenu }"
                    />
                  </button>
                  <div
                    v-if="showCommentsFilterDisplayMenu"
                    v-on-clickaway="openCommentsFilterDisplayMenu"
                    class="fixed inset-0 z-[100] bg-black/5 dark:bg-black/20 backdrop-blur-[1px]"
                    @click="openCommentsFilterDisplayMenu"
                  >
                    <div
                      :style="filterDropdownStyle"
                      class="fixed animate-slide-down"
                      @click.stop
                    >
                      <CommentsFilter
                        :initial-state="filter"
                        :custom-classifications="customClassifications"
                        :max-content-height="filterContentMaxHeight"
                        @filter="onFilterChange"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Comments List -->
          <div
            class="bg-white dark:bg-n-solid-2 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden"
          >
            <!-- Header -->
            <div
              class="px-4 sm:px-6 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800"
            >
              <h3 class="text-sm font-semibold text-slate-900 dark:text-white">
                {{ $t('COMMENTS.POST_DETAILS.ALL_COMMENTS') }}
              </h3>
            </div>

            <!-- Loading State -->
            <div v-if="isCommentsShellLoading" class="p-8">
              <woot-loading-state
                :message="$t('COMMENTS.POST_DETAILS.LOADING_COMMENTS')"
              />
            </div>

            <!-- Empty State -->
            <div
              v-else-if="
                !commentsList.comments?.length && pagination.currentPage === 1
              "
              class="p-8 text-center text-slate-700 dark:text-slate-100"
            >
              {{ $t('COMMENTS.POST_DETAILS.NO_COMMENTS') }}
            </div>

            <!-- Comments List with Scroll -->
            <div
              v-else
              ref="commentsListRef"
              class="max-h-[600px] overflow-y-auto"
            >
              <div class="divide-y divide-slate-200 dark:divide-slate-700">
                <div
                  v-for="comment in commentsList.comments"
                  :key="comment.id"
                  class="transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                >
                  <Comment
                    :comment="comment"
                    :ui-flags="uiFlags"
                    :hide-comment="id => hideComment(id)"
                    :delete-comment="id => deleteComment(id)"
                    :selected="false"
                    :open-reply-popup="openReplyPopup"
                    :open-dm-popup="openDmPopup"
                  />
                </div>
              </div>

              <!-- Loading More Indicator -->
              <div
                v-if="isLoadingMore"
                class="p-6 flex items-center justify-center bg-slate-50 dark:bg-slate-800/50"
              >
                <div
                  class="flex items-center gap-2 text-slate-600 dark:text-slate-400"
                >
                  <svg
                    class="animate-spin h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span class="text-sm">{{
                    $t('COMMENTS.POST_DETAILS.LOADING_MORE')
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </CommentsLayout>

  <!-- Reply Dialog -->
  <ReplyDialog
    ref="replyDialog"
    type="edit"
    :title="$t('COMMENTS.CARD.REPLY_TO', { name: replyUsername })"
    :confirm-button-label="$t('COMMENTS.CARD.SEND')"
    :cancel-button-label="$t('COMMENTS.CARD.CANCEL')"
    :disable-confirm-button="!replyText.trim() || replyText.length > 500"
    :is-loading="isSubmittingReply"
    width="lg"
    @confirm="submitReply"
    @close="closeReplyPopup"
  >
    <TextArea
      v-model="replyText"
      :placeholder="$t('COMMENTS.CARD.REPLY_PLACEHOLDER')"
      :max-length="500"
      :disabled="isSubmittingReply"
      show-character-count
      auto-height
      autofocus
      min-height="8rem"
      max-height="16rem"
    />
  </ReplyDialog>

  <ReplyDialog
    ref="dmDialog"
    type="edit"
    :title="
      $t('COMMENTS.MODERATION_CARD.SEND_DM_TO', {
        name: dmAuthorName || '—',
      })
    "
    :confirm-button-label="$t('COMMENTS.MODERATION_CARD.SEND')"
    :cancel-button-label="$t('COMMENTS.MODERATION_CARD.CANCEL')"
    :disable-confirm-button="!dmText.trim() || dmText.length > 1000"
    :is-loading="isSubmittingDm"
    width="lg"
    @confirm="submitDm"
    @close="closeDmPopup"
  >
    <TextArea
      v-model="dmText"
      :placeholder="$t('COMMENTS.MODERATION_CARD.DM_PLACEHOLDER')"
      :max-length="1000"
      :disabled="isSubmittingDm"
      show-character-count
      auto-height
      autofocus
      min-height="8rem"
      max-height="16rem"
    />
  </ReplyDialog>

  <!-- AI Test Chat Modal -->
  <woot-modal v-model:show="showAITestChat" :on-close="hideAITestChat">
    <AITestChat :key="postInfo?.id" :post="postInfo" />
  </woot-modal>

  <!-- Deleted comment: confirm remove from system -->
  <ReplyDialog
    ref="deletedCommentDialog"
    type="alert"
    :title="deletedCommentDialogCopy.title"
    :description="deletedCommentDialogCopy.description"
    :confirm-button-label="t('COMMENTS.MODERATION.REPLY_FAILED.CONFIRM')"
    show-cancel-button
    :cancel-button-label="t('COMMENTS.MODERATION.REPLY_FAILED.CANCEL')"
    @confirm="confirmRemoveDeletedComment"
    @close="cancelRemoveDeletedComment"
  />

  <!-- Deleted post: confirm remove from system -->
  <woot-modal
    v-model:show="showRemovePostModal"
    :on-close="closeRemovePostModal"
  >
    <div class="flex flex-col gap-4 p-4">
      <h3 class="text-base font-semibold text-slate-900 dark:text-white">
        {{ $t('COMMENTS.POST_DETAILS.DELETED_POST_TITLE') }}
      </h3>
      <p class="text-sm text-slate-600 dark:text-slate-400">
        {{ $t('COMMENTS.POST_DETAILS.DELETED_POST_MESSAGE') }}
      </p>
      <div class="flex justify-end gap-2 mt-2">
        <button
          type="button"
          class="woot-button secondary"
          :disabled="isRemovingPost"
          @click="closeRemovePostModal"
        >
          {{ $t('COMMENTS.POST_DETAILS.DELETED_POST_CANCEL') }}
        </button>
        <button
          type="button"
          class="woot-button danger"
          :disabled="isRemovingPost"
          @click="confirmRemovePost"
        >
          {{ $t('COMMENTS.POST_DETAILS.REMOVE_POST') }}
        </button>
      </div>
    </div>
  </woot-modal>
</template>

<style scoped>
@keyframes slide-down {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slide-down {
  animation: slide-down 0.2s ease-out;
}
</style>
